import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Lightbulb, FileText, Users, Paperclip, Calendar, Download, File, MessageSquare, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchIdeas } from '../../api/idea';
import { fetchIdeaFeedback } from '../../api/ideaFeedback';

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';

type TabKey = 'Idea Details' | 'Feedback' | 'Status';

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'Idea Details', label: 'Idea Details' },
  { key: 'Feedback', label: 'Feedback' },
  { key: 'Status', label: 'Status' },
];

function FeedbackContent({ feedback, stats, feedbackLoading }: { feedback?: any[]; stats?: any; feedbackLoading?: boolean }) {
  if (feedbackLoading) {
    return (
      <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-gray-600 text-sm">Loading feedback...</p>
      </div>
    );
  }

  if (!feedback || feedback.length === 0) {
    return (
      <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm text-center">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 text-sm">No feedback submitted yet. Mentors can add their feedback here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      <div className="space-y-3 sm:space-y-4">
        {feedback.map((item: any, index: number) => (
          <div key={index} className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 text-sm sm:text-base break-words">{item.mentorId?.name || 'Anonymous Mentor'}</p>
                <p className="text-xs text-gray-500 break-words">{item.mentorId?.email}</p>
              </div>
              {item.rating && (
                <div className="flex gap-1 flex-shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${i < item.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              )}
            </div>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">{item.comment}</p>
            <p className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleDateString()} at {new Date(item.createdAt).toLocaleTimeString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusContent({ ideaStatus }: { ideaStatus?: string }) {
  const getStatusInfo = (status: string | undefined) => {
    const statusMap: Record<string, { label: string; color: string; description: string; badge: string }> = {
      submitted_initial: {
        label: 'Submitted (Initial)',
        color: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-800',
        description: 'The idea has been submitted for initial review by mentors.'
      },
      under_mentor_review: {
        label: 'Under Mentor Review',
        color: 'text-yellow-600',
        badge: 'bg-yellow-100 text-yellow-800',
        description: 'Mentors are currently reviewing and providing feedback on the idea.'
      },
      submitted_final: {
        label: 'Submitted (Final)',
        color: 'text-purple-600',
        badge: 'bg-purple-100 text-purple-800',
        description: 'The team has submitted their final version after mentor feedback.'
      },
      selected: {
        label: 'Selected',
        color: 'text-green-600',
        badge: 'bg-green-100 text-green-800',
        description: 'This idea has been selected by the judging panel. Congratulations!'
      },
      rejected: {
        label: 'Rejected',
        color: 'text-red-600',
        badge: 'bg-red-100 text-red-800',
        description: 'Unfortunately, this idea was not selected to move forward.'
      }
    };
    return statusMap[status || 'submitted_initial'];
  };

  if (!ideaStatus) {
    return (
      <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm text-center">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 text-sm">No idea status available</p>
      </div>
    );
  }

  const info = getStatusInfo(ideaStatus);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border-l-4" style={{ borderLeftColor: info.color.split('-')[1] === 'blue' ? '#3b82f6' : info.color.split('-')[1] === 'yellow' ? '#eab308' : info.color.split('-')[1] === 'purple' ? '#a855f7' : info.color.split('-')[1] === 'green' ? '#22c55e' : '#ef4444' }}>
        <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4 flex-wrap">
          <div className={`inline-block px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium ${info.badge}`}>
            {info.label}
          </div>
        </div>
        <p className="text-gray-700 text-sm leading-relaxed mb-3 sm:mb-4">{info.description}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Current Stage</p>
          <p className="text-base sm:text-lg font-semibold text-blue-600 break-words">{info.label}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Status Type</p>
          <p className="text-base sm:text-lg font-semibold text-gray-900 capitalize">{ideaStatus.replace(/_/g, ' ')}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-200">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Last Updated</p>
          <p className="text-base sm:text-lg font-semibold text-green-600">{new Date().toLocaleDateString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Review Process</h3>
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-600 flex-shrink-0">1</div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm">Initial Submission</p>
              <p className="text-xs sm:text-sm text-gray-600">Team submits their idea</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${ideaStatus === 'submitted_initial' || ['under_mentor_review', 'submitted_final', 'selected', 'rejected'].includes(ideaStatus) ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'}`}>2</div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm">Mentor Review</p>
              <p className="text-xs sm:text-sm text-gray-600">Mentors provide feedback</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${['submitted_final', 'selected', 'rejected'].includes(ideaStatus) ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'}`}>3</div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm">Final Submission</p>
              <p className="text-xs sm:text-sm text-gray-600">Team resubmits improved idea</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${['selected', 'rejected'].includes(ideaStatus) ? (ideaStatus === 'selected' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600') : 'bg-gray-100 text-gray-600'}`}>4</div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 text-sm">Final Decision</p>
              <p className="text-xs sm:text-sm text-gray-600">{ideaStatus === 'selected' ? 'Idea selected!' : ideaStatus === 'rejected' ? 'Idea not selected' : 'Pending final review'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamChatContent() {
  return (
    <div className="bg-white rounded-lg p-8 shadow-sm">
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Team Chat</h2>
      <p className="text-gray-700 leading-relaxed">
        This area is reserved for team communications. Chat messages and collaboration details will appear here.
      </p>
    </div>
  );
}

function IdeaDetailsContent({ idea, teamMembers }: { idea?: any; teamMembers?: any[] }) {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500'];

  if (!idea) {
    return (
      <div className="bg-white rounded-lg p-8 shadow-sm text-center">
        <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600">No idea submitted by this team yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm">
        <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
          <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <h2 className="text-base sm:text-xl">Idea Title</h2>
        </div>
        <p className="text-gray-700 leading-relaxed text-base sm:text-lg font-semibold break-words">{idea.title}</p>
      </div>

      <div className="bg-white rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm">
        <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
          <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <h2 className="text-base sm:text-xl">Idea Statement</h2>
        </div>
        <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {idea.problem}
        </p>
      </div>

      <div className="bg-white rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm">
        <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
          <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <h2 className="text-base sm:text-xl">Solution Description</h2>
        </div>

        <p className="text-gray-700 leading-relaxed text-sm sm:text-base mb-4">
          {idea.solution}
        </p>
      </div>

      {idea.attachments && idea.attachments.length > 0 && (
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm">
          <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Paperclip className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <h2 className="text-base sm:text-xl">Attachments</h2>
          </div>

          <div className="space-y-2 sm:space-y-3">
            {idea.attachments.map((attachment: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 sm:p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors gap-2">
                <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                  <File className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-xs sm:text-sm break-words">{attachment.filename || attachment.type}</p>
                    {attachment.sizeBytes && (
                      <p className="text-xs text-gray-500">{(attachment.sizeBytes / 1024).toFixed(2)} KB</p>
                    )}
                  </div>
                </div>
                {attachment.url && (
                  <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0">
                    <Download className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Idea Status</p>
            <p className="text-gray-900 font-medium capitalize text-sm sm:text-base">{idea.status?.replace(/_/g, ' ') || 'Pending'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Submitted On</p>
            <p className="text-gray-900 text-sm sm:text-base">{new Date(idea.submittedAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {teamMembers && teamMembers.length > 0 && (
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
          <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 flex-shrink-0 mt-0.5" />
            <h2 className="text-base sm:text-xl">Team Members</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {teamMembers.map((member: any, index: number) => (
              <div key={index} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg bg-gray-50">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${colors[index % colors.length]} flex items-center justify-center text-white font-medium text-xs sm:text-sm flex-shrink-0`}>
                  {getInitials(member.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 text-sm break-words">{member.name}</p>
                  <p className="text-xs text-gray-600 break-words">{member.email}</p>
                  {member.jobRole && <p className="text-xs text-gray-500">{member.jobRole}</p>}
                  {member.status && (
                    <p className={`text-xs font-medium ${
                      member.status === 'accepted' ? 'text-green-600' :
                      member.status === 'rejected' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {member.status}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export function TeamDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId } = useParams<{ teamId: string }>();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('Idea Details');
  const [team, setTeam] = useState<any>(location.state?.team ?? null);
  const [teamLoading, setTeamLoading] = useState(!location.state?.team);
  const [idea, setIdea] = useState<any>(null);
  const [ideaLoading, setIdeaLoading] = useState(true);
  const [ideaError, setIdeaError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [feedbackStats, setFeedbackStats] = useState<any>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);

  // Fetch team from backend if not passed via state
  useEffect(() => {
    if (team || !teamId || !token) { setTeamLoading(false); return; }
    fetch(`${BASE}/teams/${teamId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then((data: any) => setTeam(data?._id ? data : null))
      .catch(() => setTeam(null))
      .finally(() => setTeamLoading(false));
  }, [teamId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!team || !token) {
      setIdeaLoading(false);
      return;
    }

    const loadIdea = async () => {
      try {
        setIdeaLoading(true);
        setIdeaError(null);
        const response = await fetchIdeas(token, { teamId: team._id });
        
        // Handle response structure: { items, pagination }
        const ideas = response?.items || [];
        const foundIdea = ideas.length > 0 ? ideas[0] : null;
        
        setIdea(foundIdea);
      } catch (err) {
        console.error('Error loading idea:', err);
        // Don't set error if it's just no ideas found - that's normal
        setIdea(null);
      } finally {
        setIdeaLoading(false);
      }
    };

    loadIdea();
  }, [team, token]);

  // Fetch feedback when idea is loaded
  useEffect(() => {
    if (!idea?._id || !token) {
      setFeedbackLoading(false);
      return;
    }

    const loadFeedback = async () => {
      try {
        setFeedbackLoading(true);
        const response = await fetchIdeaFeedback(token, idea._id);
        setFeedback(response?.items || []);
        setFeedbackStats(response?.stats || null);
      } catch (err) {
        console.error('Error loading feedback:', err);
        setFeedback([]);
        setFeedbackStats(null);
      } finally {
        setFeedbackLoading(false);
      }
    };

    loadFeedback();
  }, [idea, token]);

  if (teamLoading) {
    return (
      <div className="w-full flex items-center justify-center px-4 py-8">
        <p className="text-gray-500 text-sm">Loading team details...</p>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="w-full" style={{ minHeight: 0, minWidth: 0 }}>
        <div className="bg-white border-b">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <button
              type="button"
              onClick={() => navigate('/mentor-dashboard/assign-team')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4 flex-shrink-0" />
              <span>Back to Assigned Teams</span>
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
          <div className="bg-white rounded-lg p-6 sm:p-8 text-center shadow-sm">
            <p className="text-red-600 font-medium text-sm sm:text-base">Team data not found. Please select a team from the list.</p>
            <button
              onClick={() => navigate('/mentor-dashboard/assign-team')}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-blue-700"
            >
              Return to Teams
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" style={{ minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="bg-white border-b flex-shrink-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <button
            type="button"
            onClick={() => navigate('/mentor-dashboard/assign-team')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span>Back to Assigned Teams</span>
          </button>

          <h1 className="text-2xl sm:text-3xl mb-2 sm:mb-3 break-words">{team.teamName}</h1>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600" style={{ minWidth: 0 }}>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Users className="w-4 h-4 flex-shrink-0" />
              <span>{team.members?.length || 0} members</span>
            </div>
            {team.leader && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <FileText className="w-4 h-4 flex-shrink-0" />
                <span>Lead: {team.leader.name}</span>
              </div>
            )}
            {team.createdAt && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Calendar className="w-4 h-4 flex-shrink-0" />
                <span>Created: {new Date(team.createdAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-b flex-shrink-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 sm:gap-3 bg-gray-100 p-2 rounded-full">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors flex-shrink-0 ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0, minWidth: 0 }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {activeTab === 'Idea Details' && (
            ideaLoading ? (
              <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">Loading idea details...</p>
              </div>
            ) : (
              <IdeaDetailsContent idea={idea} teamMembers={team.members} />
            )
          )}
          {activeTab === 'Feedback' && <FeedbackContent feedback={feedback} stats={feedbackStats} feedbackLoading={feedbackLoading} />}
          {activeTab === 'Status' && <StatusContent ideaStatus={idea?.status} />}
          {/* Team Chat has been removed from mentor team details */}
        </div>
      </div>
    </div>
  );
}
